import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, Trash2, Save, RefreshCw, ChevronDown, ChevronUp, FolderPlus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  getMenu, createMenuCategory, patchMenuCategory, deleteMenuCategory,
  addMenuItem, updateMenuItem, deleteMenuItem,
} from '../services/api';

const emptyItem = { name: '', description: '', price: '', image: '' };

const MenuTab = () => {
  const { toast } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [draftItems, setDraftItems] = useState({}); // { [catId]: {...emptyItem} }
  const [newCategory, setNewCategory] = useState({ id: '', name: '', description: '' });
  const [showNewCategory, setShowNewCategory] = useState(false);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const data = await getMenu();
      setCategories(data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar el menú', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMenu(); }, []);

  const toggleExpanded = (catId) => setExpanded((p) => ({ ...p, [catId]: !p[catId] }));

  const handleSaveCategoryMeta = async (cat) => {
    try {
      await patchMenuCategory(cat.id, { name: cat.name, description: cat.description });
      toast({ title: '✅ Categoría guardada', description: `"${cat.name}" actualizada.` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('¿Eliminar esta categoría y todos sus platillos? Esta acción no se puede deshacer.')) return;
    try {
      await deleteMenuCategory(catId);
      setCategories((p) => p.filter((c) => c.id !== catId));
      toast({ title: '🗑️ Categoría eliminada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleCreateCategory = async () => {
    const id = (newCategory.id || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (!id || !newCategory.name.trim()) {
      toast({ title: 'Error', description: 'ID y nombre son obligatorios', variant: 'destructive' });
      return;
    }
    try {
      const created = await createMenuCategory({
        id, name: newCategory.name.trim(), description: newCategory.description.trim(), items: [],
      });
      setCategories((p) => [...p, created]);
      setNewCategory({ id: '', name: '', description: '' });
      setShowNewCategory(false);
      setExpanded((p) => ({ ...p, [id]: true }));
      toast({ title: '✅ Categoría creada', description: created.name });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleAddItem = async (catId) => {
    const draft = draftItems[catId] || emptyItem;
    if (!draft.name.trim() || draft.price === '') {
      toast({ title: 'Error', description: 'Nombre y precio son obligatorios', variant: 'destructive' });
      return;
    }
    try {
      const created = await addMenuItem(catId, {
        name: draft.name.trim(),
        description: draft.description,
        price: parseFloat(draft.price) || 0,
        image: draft.image,
      });
      setCategories((p) => p.map((c) => (c.id === catId ? { ...c, items: [...c.items, created] } : c)));
      setDraftItems((p) => ({ ...p, [catId]: { ...emptyItem } }));
      toast({ title: '✅ Platillo agregado', description: created.name });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleSaveItem = async (catId, item) => {
    try {
      const updated = await updateMenuItem(catId, item.id, {
        name: item.name, description: item.description, price: parseFloat(item.price) || 0, image: item.image,
      });
      setCategories((p) => p.map((c) =>
        c.id === catId ? { ...c, items: c.items.map((i) => (i.id === updated.id ? updated : i)) } : c
      ));
      toast({ title: '✅ Platillo guardado', description: updated.name });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (catId, itemId, itemName) => {
    if (!window.confirm(`¿Eliminar "${itemName}"?`)) return;
    try {
      await deleteMenuItem(catId, itemId);
      setCategories((p) => p.map((c) =>
        c.id === catId ? { ...c, items: c.items.filter((i) => i.id !== itemId) } : c
      ));
      toast({ title: '🗑️ Platillo eliminado' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const updateItemField = (catId, itemId, field, value) => {
    setCategories((p) => p.map((c) =>
      c.id === catId ? { ...c, items: c.items.map((i) => (i.id === itemId ? { ...i, [field]: value } : i)) } : c
    ));
  };

  const updateDraftField = (catId, field, value) => {
    setDraftItems((p) => ({ ...p, [catId]: { ...(p[catId] || emptyItem), [field]: value } }));
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="menu-tab">
      {/* New category control */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Gestión del Menú</CardTitle>
            <CardDescription>{categories.length} categorías · {categories.reduce((s, c) => s + (c.items?.length || 0), 0)} platillos</CardDescription>
          </div>
          <Button onClick={() => setShowNewCategory((v) => !v)} variant="outline" data-testid="toggle-new-category">
            <FolderPlus className="w-4 h-4 mr-2" />Nueva categoría
          </Button>
        </CardHeader>
        {showNewCategory && (
          <CardContent className="space-y-3 border-t pt-4" data-testid="new-category-form">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>ID (slug) *</Label>
                <Input value={newCategory.id} onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value })} placeholder="ej: postres" data-testid="new-category-id" />
              </div>
              <div className="md:col-span-2">
                <Label>Nombre *</Label>
                <Input value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} placeholder="Ej: Postres Finos" data-testid="new-category-name" />
              </div>
            </div>
            <div>
              <Label>Descripción</Label>
              <Input value={newCategory.description} onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })} placeholder="Breve descripción de la categoría" data-testid="new-category-description" />
            </div>
            <Button onClick={handleCreateCategory} className="bg-black hover:bg-gray-800" data-testid="create-category-btn">
              <Plus className="w-4 h-4 mr-2" />Crear categoría
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Categories list */}
      {categories.map((cat) => {
        const isOpen = !!expanded[cat.id];
        const draft = draftItems[cat.id] || emptyItem;
        return (
          <Card key={cat.id} data-testid={`menu-category-${cat.id}`}>
            <CardHeader className="cursor-pointer" onClick={() => toggleExpanded(cat.id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  <div>
                    <CardTitle className="text-lg">{cat.name}</CardTitle>
                    <CardDescription>{cat.description}</CardDescription>
                  </div>
                </div>
                <Badge variant="outline">{cat.items?.length || 0} platillos</Badge>
              </div>
            </CardHeader>
            {isOpen && (
              <CardContent className="space-y-4 border-t pt-4">
                {/* Category meta editor */}
                <div className="bg-pink-50 p-3 rounded-lg space-y-2">
                  <Label className="text-xs font-semibold text-gray-700">Editar categoría</Label>
                  <Input value={cat.name} onChange={(e) => setCategories((p) => p.map((c) => c.id === cat.id ? { ...c, name: e.target.value } : c))} placeholder="Nombre" data-testid={`cat-name-${cat.id}`} />
                  <Input value={cat.description} onChange={(e) => setCategories((p) => p.map((c) => c.id === cat.id ? { ...c, description: e.target.value } : c))} placeholder="Descripción" data-testid={`cat-desc-${cat.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveCategoryMeta(cat)} className="bg-black hover:bg-gray-800" data-testid={`save-cat-${cat.id}`}>
                      <Save className="w-4 h-4 mr-1" />Guardar
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                      <Trash2 className="w-4 h-4 mr-1" />Eliminar categoría
                    </Button>
                  </div>
                </div>

                {/* Items */}
                {(cat.items || []).map((item) => (
                  <div key={item.id} className="border rounded-lg p-3 space-y-2" data-testid={`menu-item-${cat.id}-${item.id}`}>
                    <div className="grid md:grid-cols-2 gap-2">
                      <Input value={item.name} onChange={(e) => updateItemField(cat.id, item.id, 'name', e.target.value)} placeholder="Nombre" />
                      <Input type="number" step="0.01" value={item.price} onChange={(e) => updateItemField(cat.id, item.id, 'price', e.target.value)} placeholder="Precio" />
                    </div>
                    <Textarea value={item.description} onChange={(e) => updateItemField(cat.id, item.id, 'description', e.target.value)} placeholder="Descripción" rows={2} />
                    <Input value={item.image || ''} onChange={(e) => updateItemField(cat.id, item.id, 'image', e.target.value)} placeholder="URL de imagen (opcional)" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveItem(cat.id, item)} className="bg-black hover:bg-gray-800" data-testid={`save-item-${cat.id}-${item.id}`}>
                        <Save className="w-4 h-4 mr-1" />Guardar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteItem(cat.id, item.id, item.name)} data-testid={`delete-item-${cat.id}-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Add item form */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid={`add-item-form-${cat.id}`}>
                  <Label className="text-xs font-semibold text-green-900">Agregar nuevo platillo</Label>
                  <div className="grid md:grid-cols-2 gap-2">
                    <Input value={draft.name} onChange={(e) => updateDraftField(cat.id, 'name', e.target.value)} placeholder="Nombre *" data-testid={`draft-name-${cat.id}`} />
                    <Input type="number" step="0.01" value={draft.price} onChange={(e) => updateDraftField(cat.id, 'price', e.target.value)} placeholder="Precio *" data-testid={`draft-price-${cat.id}`} />
                  </div>
                  <Textarea value={draft.description} onChange={(e) => updateDraftField(cat.id, 'description', e.target.value)} placeholder="Descripción" rows={2} data-testid={`draft-desc-${cat.id}`} />
                  <Input value={draft.image} onChange={(e) => updateDraftField(cat.id, 'image', e.target.value)} placeholder="URL de imagen (opcional)" data-testid={`draft-image-${cat.id}`} />
                  <Button size="sm" onClick={() => handleAddItem(cat.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`add-item-btn-${cat.id}`}>
                    <Plus className="w-4 h-4 mr-1" />Agregar platillo
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default MenuTab;
